import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Mail, Send, Brain, Copy, Printer, FileText, UserCheck,
  Pencil, Mic, MicOff, CheckCircle2, Camera,
} from 'lucide-react';
import { SUGGESTED_CODES, TOUR_STEPS } from './doctorModeData';
import { useAppMode } from '../../../contexts/AppModeContext';

/* ═══ All inline dialogs extracted from LuminaDoctorMode ═══ */
export const DoctorDialogs = ({
  patient, soapContent,
  showDM, setShowDM, dmRecipient, setDmRecipient,
  showCoding, setShowCoding,
  showInstructions, setShowInstructions,
  showSummary, setShowSummary, summaryEmail, setSummaryEmail,
  reviewingNote, setReviewingNote, reviewEdits, setReviewEdits,
  editingSections, setEditingSections, reviewDictating, setReviewDictating,
  requestCopy, onCopyCodes, setShowLens,
}) => {
  const { isDemo } = useAppMode();
  return (
  <>
    {/* Direct Message */}
    <Dialog open={showDM} onOpenChange={setShowDM}>
      <DialogContent className="max-w-lg" data-testid="dm-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4" /> Send Direct Message</DialogTitle>
          <DialogDescription>Send SOAP note via secure Direct Messaging.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label className="text-xs">Recipient</Label><Input placeholder="dr.smith@direct.hospital.org" value={dmRecipient} onChange={e => setDmRecipient(e.target.value)} className="mt-1" data-testid="dm-input" /></div>
          <div className="bg-slate-50 rounded-lg border p-3 max-h-28 overflow-y-auto">
            <p className="text-[10px] font-semibold text-slate-500 mb-1">Preview</p>
            <p className="text-[11px] text-slate-500 font-mono whitespace-pre-wrap">{soapContent ? `SOAP — ${patient?.firstName} ${patient?.lastName}\n\nSUBJECTIVE:\n${soapContent.subjective?.slice(0, 180)}...` : 'No note'}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { toast.success('Direct Message sent', { description: `Sent to ${dmRecipient}` }); setShowDM(false); setDmRecipient(''); }}
              disabled={!dmRecipient.trim()} className="flex-1 bg-black hover:bg-gray-800 text-white gap-2 text-xs" data-testid="dm-send-btn"><Send className="h-3.5 w-3.5" /> Send</Button>
            <Button variant="outline" onClick={() => setShowDM(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Coding */}
    <Dialog open={showCoding} onOpenChange={setShowCoding}>
      <DialogContent className="max-w-lg" data-testid="coding-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Brain className="h-4 w-4" /> Coding Suggestions</DialogTitle></DialogHeader>
        <div className="space-y-2 mt-2">
          {SUGGESTED_CODES.map(c => (
            <div key={c.code} className="flex items-center justify-between p-3 rounded-lg border text-sm">
              <div className="flex items-center gap-2"><Badge variant="outline" className="font-mono text-xs">{c.code}</Badge><span className="text-xs text-slate-700">{c.desc}</span></div>
              <div className="flex items-center gap-2"><Badge className="bg-slate-50 text-slate-600 text-[10px] border-0">{c.type}</Badge><Badge className="bg-slate-100 text-slate-600 text-[10px] border-0">{c.confidence}%</Badge></div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3"><Button size="sm" variant="outline" onClick={() => { onCopyCodes(); setShowCoding(false); }} className="gap-1.5 text-xs"><Copy className="h-3 w-3" /> Copy All</Button></div>
      </DialogContent>
    </Dialog>

    {/* Instructions */}
    <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
      <DialogContent className="max-w-lg" data-testid="instructions-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><UserCheck className="h-4 w-4" /> Patient Instructions</DialogTitle></DialogHeader>
        <div className="whitespace-pre-wrap text-sm bg-slate-50 border rounded-lg p-4 font-mono leading-relaxed mt-2 max-h-72 overflow-y-auto">
{`Visit Summary — ${patient?.firstName} ${patient?.lastName} — ${new Date().toLocaleDateString()}

Diagnosis: Tension-type headache; Elevated blood pressure

What we discussed:
Your headaches are most likely tension headaches related to stress. Your blood pressure was slightly elevated today.

Medications:
- Acetaminophen (Tylenol) 500mg every 6 hours as needed

What you should do:
- Practice stress management techniques
- Reduce sodium in your diet
- Exercise at least 30 minutes most days

Follow-up:
- Return in 2 weeks for blood pressure recheck`}
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => requestCopy('Visit Summary...', 'Instructions')}><Copy className="h-3.5 w-3.5" /> Copy</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.success('Sent to printer')}><Printer className="h-3.5 w-3.5" /> Print</Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Quick Summary */}
    <Dialog open={showSummary} onOpenChange={setShowSummary}>
      <DialogContent className="max-w-lg" data-testid="summary-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4" /> Quick Encounter Summary</DialogTitle>
          <DialogDescription>Send a patient-friendly visit summary via email.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label className="text-xs">Patient Email</Label><Input placeholder={patient?.email || 'patient@email.com'} value={summaryEmail} onChange={e => setSummaryEmail(e.target.value)} className="mt-1" data-testid="summary-email-input" /></div>
          <div className="bg-slate-50 rounded-lg border p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Preview</p>
            <div className="text-xs text-slate-600 space-y-2">
              <p className="font-medium">Hi {patient?.firstName},</p>
              <p>Thank you for your visit on {new Date().toLocaleDateString()}. Here's a summary of what we discussed:</p>
              <p className="font-medium">Diagnosis: Tension-type headache; Elevated blood pressure</p>
              <p>We've prescribed Acetaminophen as needed. Please return in 2 weeks for a BP recheck.</p>
              <p className="text-slate-400">— {isDemo ? "Dr. Chen's Office" : 'Your provider'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { toast.success('Summary sent', { description: `Emailed to ${summaryEmail}` }); setShowSummary(false); setSummaryEmail(''); }}
              disabled={!summaryEmail.trim()} className="flex-1 bg-black hover:bg-gray-800 text-white gap-2 text-xs" data-testid="send-summary-btn"><Send className="h-3.5 w-3.5" /> Send</Button>
            <Button variant="outline" onClick={() => setShowSummary(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Encounter Review Lightbox */}
    <Dialog open={!!reviewingNote} onOpenChange={(open) => { if (!open) { setReviewingNote(null); setReviewEdits({}); setEditingSections({}); setReviewDictating(null); } }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0 max-h-[90vh]" data-testid="note-review-lightbox" aria-describedby="note-review-desc">
        {reviewingNote && (
          <>
            {/* Header */}
            <div className="bg-slate-900 px-5 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-white" />
                <div>
                  <DialogTitle className="text-sm font-bold text-white">{reviewingNote.chief}</DialogTitle>
                  <DialogDescription id="note-review-desc" className="sr-only">Review and edit SOAP note</DialogDescription>
                  <p className="text-[10px] text-slate-400 mt-0.5">{reviewingNote.date} &middot; {reviewingNote.provider} &middot; {patient?.firstName} {patient?.lastName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={cn("text-[9px] border-0", reviewingNote.status === 'Signed' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')}>{reviewingNote.status || 'Draft'}</Badge>
              </div>
            </div>

            {/* Action Bar */}
            <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => requestCopy(Object.entries(reviewEdits).map(([k, v]) => `${k.toUpperCase()}:\n${v}`).join('\n\n'), 'SOAP Note')} data-testid="review-copy-btn">
                <Copy className="h-2.5 w-2.5" /> Copy Full Note
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => setShowLens(true)} data-testid="review-lens-btn">
                <Camera className="h-2.5 w-2.5" /> Enrich via Lens
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => {
                const anyEditing = ['subjective', 'objective', 'assessment', 'plan'].some(k => editingSections[`review_${k}`]);
                const newState = {};
                ['subjective', 'objective', 'assessment', 'plan'].forEach(k => { newState[`review_${k}`] = !anyEditing; });
                setEditingSections(p => ({ ...p, ...newState }));
              }} data-testid="review-edit-all-btn">
                <Pencil className="h-2.5 w-2.5" /> {['subjective', 'objective', 'assessment', 'plan'].some(k => editingSections[`review_${k}`]) ? 'Done Editing' : 'Edit All'}
              </Button>
              <div className="flex-1" />
              <Button size="sm" className="h-7 text-[10px] gap-1 bg-black text-white hover:bg-gray-800" onClick={() => { toast.success('Note finalized and saved'); setReviewingNote(null); setReviewEdits({}); setEditingSections({}); setReviewDictating(null); }} data-testid="review-finalize-btn">
                <CheckCircle2 className="h-2.5 w-2.5" /> Finalize Note
              </Button>
            </div>

            {/* SOAP Sections */}
            <div className="overflow-y-auto max-h-[60vh] p-5 space-y-3">
              {['Subjective', 'Objective', 'Assessment', 'Plan'].map(section => {
                const key = section.toLowerCase();
                const isEditing = editingSections[`review_${key}`];
                const isDictating = reviewDictating === key;
                return (
                  <div key={section} className={cn("rounded-xl border overflow-hidden transition-all", isDictating ? "border-violet-300 shadow-sm shadow-violet-100/50" : "border-slate-200")} data-testid={`review-section-${key}`}>
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-50/60">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{section}</p>
                      <div className="flex items-center gap-1.5">
                        {/* Dictate button */}
                        <button
                          onClick={() => {
                            if (isDictating) {
                              setReviewDictating(null);
                              toast.success('Dictation captured', { description: `${section} updated` });
                            } else {
                              setReviewDictating(key);
                              setEditingSections(p => ({ ...p, [`review_${key}`]: true }));
                              toast.info('Dictating...', { description: `Speak to edit ${section}` });
                            }
                          }}
                          className={cn("text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded-md transition-colors", isDictating ? "bg-violet-100 text-violet-700" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100")}
                          data-testid={`review-dictate-${key}`}
                        >
                          {isDictating ? <><MicOff className="h-2.5 w-2.5" /> Stop</> : <><Mic className="h-2.5 w-2.5" /> Dictate</>}
                        </button>
                        <div className="h-3 w-px bg-slate-200" />
                        <button onClick={() => requestCopy(reviewEdits[key] || '', section)} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                          <Copy className="h-2.5 w-2.5" /> Copy
                        </button>
                        <button onClick={() => setEditingSections(p => ({ ...p, [`review_${key}`]: !p[`review_${key}`] }))} className={cn("text-[10px] flex items-center gap-0.5", isEditing ? "text-violet-600 font-semibold" : "text-slate-400 hover:text-slate-600")} data-testid={`review-edit-${key}`}>
                          <Pencil className="h-2.5 w-2.5" /> {isEditing ? 'Done' : 'Edit'}
                        </button>
                      </div>
                    </div>
                    {isDictating && (
                      <div className="px-4 py-1.5 bg-violet-50 border-b border-violet-100 flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
                        <p className="text-[10px] text-violet-700 font-medium">Recording — speak your edits for {section.toLowerCase()}</p>
                      </div>
                    )}
                    {isEditing ? (
                      <div className="p-4">
                        <Textarea
                          value={reviewEdits[key] || ''}
                          onChange={e => setReviewEdits(p => ({ ...p, [key]: e.target.value }))}
                          className="min-h-28 font-mono text-sm border-0 shadow-none focus-visible:ring-0 p-0"
                          placeholder={`Edit ${section.toLowerCase()}...`}
                          data-testid={`review-textarea-${key}`}
                        />
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {reviewEdits[key] || <span className="text-slate-400 italic">No content</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

/* ═══ Tour Overlay ═══ */
export const TourOverlay = ({ tourActive, tourStep, setTourStep, skipTour, nextTour }) => {
  if (!tourActive || tourStep >= TOUR_STEPS.length) return null;
  return (
    <>
      {/* Dark overlay */}
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={skipTour} data-testid="tour-overlay" />
      {/* Tooltip card */}
      <div className="fixed z-[70] animate-in fade-in duration-200" style={{
        top: tourStep <= 1 ? '25%' : tourStep <= 3 ? '35%' : tourStep === 4 ? '40%' : '30%',
        left: tourStep >= 2 && tourStep <= 3 ? '60%' : '40%',
        transform: 'translate(-50%, -50%)',
      }} data-testid="tour-tooltip">
        <div className="bg-white rounded-xl p-5 shadow-2xl max-w-[280px]" style={{ border: '2px solid #6b5ce7' }}>
          {/* Step counter with progress dots */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold text-[#6b5ce7]">Step {TOUR_STEPS[tourStep].step} of {TOUR_STEPS.length}</span>
            <div className="flex gap-1">
              {TOUR_STEPS.map((_, i) => (
                <div key={i} className={cn("h-1.5 rounded-full transition-all", i === tourStep ? "w-4 bg-[#6b5ce7]" : i < tourStep ? "w-1.5 bg-[#6b5ce7]/40" : "w-1.5 bg-slate-200")} />
              ))}
            </div>
          </div>
          <p className="text-sm font-semibold text-slate-900 mb-2">{TOUR_STEPS[tourStep].title}</p>
          <p className="text-xs text-slate-500 leading-relaxed">{TOUR_STEPS[tourStep].desc}</p>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
            <button onClick={skipTour} className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-skip">Skip tour</button>
            <div className="flex gap-2">
              {tourStep > 0 && (
                <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => setTourStep(tourStep - 1)} data-testid="tour-prev">Back</Button>
              )}
              <Button size="sm" className="h-7 px-4 bg-black hover:bg-gray-800 text-xs" onClick={nextTour} data-testid="tour-next">
                {tourStep === TOUR_STEPS.length - 1 ? 'Done' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
